
import type { Column, ChartConfig, Dataset, DataRelationship } from '../types';
import Papa from 'papaparse';
import { applyDataPrepPipeline } from './dataPrepEngine';

/**
 * Gets the transformed data for a dataset, applying any Data Prep steps.
 * Returns raw sampleRows if no prep steps are configured.
 */
const getTransformedData = (dataset: Dataset): Record<string, any>[] => {
    if (!dataset.prepSteps || dataset.prepSteps.length === 0) {
        return dataset.sampleRows || dataset.data || [];
    }

    const rawData = dataset.sampleRows || dataset.data || [];
    const baseSchema = dataset.schema.map(c => ({ name: c.name, type: c.type }));

    const result = applyDataPrepPipeline(
        dataset.id,
        rawData,
        baseSchema,
        dataset.prepSteps,
        { previewRowLimit: Infinity } // Use full data for charts
    );

    return result.data;
};

// Simple type detection heuristic
const detectType = (value: string): 'number' | 'date' | 'string' => {
    if (!isNaN(parseFloat(value)) && isFinite(value as any)) {
        return 'number';
    }
    // Basic date check (YYYY-MM-DD or MM/DD/YYYY)
    if (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        if (!isNaN(new Date(value).getTime())) {
            return 'date';
        }
    }
    return 'string';
};

export const parseCSV = (file: File): Promise<{ data: Record<string, any>[], schema: Column[] }> => {
    return new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true, // PapaParse will attempt to convert types
            complete: (results: any) => {
                if (results.errors.length) {
                    return reject(results.errors);
                }

                const data = results.data as Record<string, any>[];
                if (data.length === 0) {
                    return reject(new Error("CSV file is empty or has no data rows."));
                }

                const firstRow = data[0];
                const schema: Column[] = results.meta.fields.map((fieldName: string) => {
                    const value = firstRow[fieldName];
                    let detectedType: Column['type'] = 'string';

                    if (typeof value === 'number') {
                        detectedType = 'number';
                    } else if (typeof value === 'boolean') {
                        detectedType = 'boolean';
                    } else if (typeof value === 'string') {
                        detectedType = detectType(value);
                    }

                    return {
                        name: fieldName,
                        type: detectedType
                    };
                });

                resolve({ data, schema });
            },
            error: (error: any) => {
                reject(error);
            }
        });
    });
};

const FACET_LIMIT = 9;

const runAggregation = (data: Record<string, any>[], config: ChartConfig): any[] => {
    const { type, xField, yField, aggregation, measures = [], ySeries, facets, encoding } = config;

    // Advanced Charts Handling (Vector, 3D, Map, Globe, Scientific Scatter)
    const isAdvancedPoint = type === 'vector' || type === '3d' || type === 'map' || type === 'globe' || (type === 'scientific' && config.advanced?.scientific?.plotType === 'scatter');

    if (isAdvancedPoint) {
        // For advanced point charts, we typically want raw data (or simple mapping), not aggregation by X.
        // However, if the user explicitly selected measures, we might want to respect that?
        // For now, let's map raw rows to points, extracting advanced fields.

        // Safety Limit
        const POINT_LIMIT = 5000;
        const slicedData = data.slice(0, POINT_LIMIT);

        return slicedData.map(row => {
            const item: any = { ...row };

            // X and Y
            if (xField) item.x = parseFloat(row[xField]);
            if (yField) item.y = parseFloat(row[yField]);

            // Advanced Fields
            if (config.advanced) {
                const { vector, threeD, map, globe, scientific } = config.advanced;

                // Vector
                if (type === 'vector' && vector) {
                    if (vector.uField) item.u = parseFloat(row[vector.uField]);
                    if (vector.vField) item.v = parseFloat(row[vector.vField]);
                }

                // 3D
                if (type === '3d' && threeD) {
                    if (threeD.xField) item.x = parseFloat(row[threeD.xField]);
                    if (threeD.yField) item.y = parseFloat(row[threeD.yField]);
                    if (threeD.zField) item.z = parseFloat(row[threeD.zField]);
                }

                // Globe
                if (type === 'globe' && globe) {
                    if (globe.latField) item.lat = parseFloat(row[globe.latField]);
                    if (globe.lonField) item.lng = parseFloat(row[globe.lonField]);
                    if (globe.valueField) item.value = parseFloat(row[globe.valueField]);
                    if (globe.categoryField) item.category = row[globe.categoryField];
                }

                // Map (separate from globe)
                if (type === 'map' && map) {
                    if (map.latField) item.lat = parseFloat(row[map.latField]);
                    if (map.lonField) item.lng = parseFloat(row[map.lonField]);
                    // Legacy top-level support
                    if (!map.latField && config.latField) item.lat = parseFloat(row[config.latField]);
                    if (!map.lonField && config.lonField) item.lng = parseFloat(row[config.lonField]);

                    // Choropleth fields
                    if (config.iso3Field) item.iso3 = row[config.iso3Field];
                    if (config.countryField) item.country = row[config.countryField];
                }

                // Scientific Error Bars (Scatter mode)
                if (type === 'scientific' && scientific?.errorBars?.show) {
                    // Assuming error fields might be passed in a specific way or we need to add them to config?
                    // The spec didn't specify where error fields come from in the new config.
                    // Let's assume they might be in the row if pre-calculated, or we need to add errorField to ScientificOptions?
                    // For now, we'll skip explicit error field extraction unless added to options.
                }
            }

            return item;
        }).filter(d => {
            // Basic validation
            if (type === '3d') return !isNaN(d.x) && !isNaN(d.y) && !isNaN(d.z);
            if (type === 'vector') return !isNaN(d.x) && !isNaN(d.y);
            if (type === 'map' || type === 'globe') {
                // For points, need lat/lng. For choropleth, need iso3/country.
                // We'll filter loosely here and let the renderer handle specifics if needed.
                return (d.lat !== undefined && !isNaN(d.lat) && d.lng !== undefined && !isNaN(d.lng)) || !!d.iso3 || !!d.country;
            }
            return !isNaN(d.x) && !isNaN(d.y);
        });
    }

    if (type === 'scatter') {
        if (!xField) return [];

        // Determine active measures
        let scatterMeasures = measures && measures.length > 0 ? measures : [];
        if (scatterMeasures.length === 0 && yField) {
            scatterMeasures = [{
                id: 'legacy_y',
                field: yField,
                aggregation: 'sum',
                displayName: config.title || 'Value',
                visible: true,
                colorMode: 'auto'
            } as any];
        }

        if (scatterMeasures.length === 0) return [];

        console.log('[SCATTER] Processing scatter plot with measures:', scatterMeasures.map(m => ({
            field: m.field,
            aggregation: m.aggregation,
            id: m.id
        })));

        // Check for encoding fields
        const colorField = encoding?.color?.field;
        const detailField = encoding?.detail?.field;
        const labelField = encoding?.label?.field;
        const groupByField = colorField || detailField; // Use color or detail for grouping

        console.log('[SCATTER] Encoding fields:', { colorField, detailField, labelField, groupByField });

        // Helper function to process scatter data for a subset
        const processScatterData = (inputData: any[]) => {
            const SCATTER_LIMIT = 5000;
            const slicedData = inputData.slice(0, SCATTER_LIMIT);

            // If grouping by color/detail, split data first
            if (groupByField) {
                const groupValues = Array.from(new Set(slicedData.map(row => String(row[groupByField] ?? '(No Value)')))).sort();

                const allSeries: any[] = [];

                scatterMeasures.filter(m => m.visible !== false).forEach(measure => {
                    groupValues.forEach((groupValue, groupIdx) => {
                        const groupData = slicedData.filter(row => String(row[groupByField] ?? '(No Value)') === groupValue);

                        const groupedByX = new Map<string, any[]>();
                        groupData.forEach(row => {
                            const xVal = row[xField];
                            if (xVal === null || xVal === undefined || xVal === '') return;
                            const key = String(xVal);
                            if (!groupedByX.has(key)) groupedByX.set(key, []);
                            groupedByX.get(key)!.push(row);
                        });

                        const points = Array.from(groupedByX.entries()).map(([key, rows]) => {
                            const x = parseFloat(rows[0][xField]);
                            if (isNaN(x)) return null;

                            let y = 0;
                            const vals = rows.map(r => parseFloat(r[measure.field])).filter(v => !isNaN(v));

                            if (measure.aggregation === 'count') {
                                y = rows.length;
                            } else {
                                if (vals.length === 0) return null;
                                switch (measure.aggregation) {
                                    case 'sum': y = vals.reduce((a, b) => a + b, 0); break;
                                    case 'avg': y = vals.reduce((a, b) => a + b, 0) / vals.length; break;
                                    case 'min': y = Math.min(...vals); break;
                                    case 'max': y = Math.max(...vals); break;
                                    default: y = vals.reduce((a, b) => a + b, 0);
                                }
                            }

                            const item: any = { x, y, category: groupValue, ...rows[0] };
                            if (config.sizeField) {
                                const sizeVals = rows.map(r => parseFloat(r[config.sizeField!])).filter(v => !isNaN(v));
                                if (sizeVals.length > 0) item.size = sizeVals.reduce((a, b) => a + b, 0) / sizeVals.length;
                            }
                            if (labelField) item.label = rows[0][labelField];
                            return item;
                        }).filter(Boolean);

                        if (points.length > 0) {
                            const seriesId = colorField ? `${measure.id}::${groupValue}` : `${measure.id}@@${groupValue}`;
                            allSeries.push({
                                seriesId,
                                label: scatterMeasures.length > 1 ? `${measure.displayName || measure.field} - ${groupValue}` : groupValue,
                                measureId: measure.id,
                                colorCategory: colorField ? groupValue : undefined,
                                groupIndex: groupIdx,
                                points
                            });
                        }
                    });
                });

                return allSeries;
            }

            // Standard scatter processing (no encoding)
            const groupedByX = new Map<string, any[]>();
            slicedData.forEach(row => {
                const xVal = row[xField];
                if (xVal === null || xVal === undefined || xVal === '') return;
                const key = String(xVal);
                if (!groupedByX.has(key)) groupedByX.set(key, []);
                groupedByX.get(key)!.push(row);
            });

            return scatterMeasures.filter(m => m.visible !== false).map(measure => {
                const points = Array.from(groupedByX.entries()).map(([key, rows]) => {
                    const x = parseFloat(rows[0][xField]);
                    if (isNaN(x)) return null;

                    let y = 0;
                    const vals = rows.map(r => parseFloat(r[measure.field])).filter(v => !isNaN(v));

                    if (measure.aggregation === 'count') {
                        y = rows.length;
                    } else {
                        if (vals.length === 0) return null;
                        switch (measure.aggregation) {
                            case 'sum': y = vals.reduce((a, b) => a + b, 0); break;
                            case 'avg': y = vals.reduce((a, b) => a + b, 0) / vals.length; break;
                            case 'min': y = Math.min(...vals); break;
                            case 'max': y = Math.max(...vals); break;
                            default: y = vals.reduce((a, b) => a + b, 0);
                        }
                    }

                    const item: any = { x, y, ...rows[0] };
                    if (config.sizeField) {
                        const sizeVals = rows.map(r => parseFloat(r[config.sizeField!])).filter(v => !isNaN(v));
                        if (sizeVals.length > 0) item.size = sizeVals.reduce((a, b) => a + b, 0) / sizeVals.length;
                    }
                    if (labelField) item.label = rows[0][labelField];
                    return item;
                }).filter(Boolean);

                return { seriesId: measure.id, label: measure.displayName || measure.field, measureId: measure.id, points };
            });
        };

        // Handle Facets for scatter
        if (facets?.field) {
            const FACET_LIMIT = 12;
            const uniqueFacets = Array.from(new Set(data.map(row => String(row[facets.field])))).sort().slice(0, FACET_LIMIT);
            console.log('[SCATTER] Facet values:', uniqueFacets);

            return uniqueFacets.map(facetVal => ({
                facet: facetVal,
                data: processScatterData(data.filter(row => String(row[facets.field]) === facetVal))
            }));
        }

        // No facets - return series directly
        return processScatterData(data);
    }

    if (type === 'heatmap') {
        console.log('[HEATMAP] Config:', {
            xCategoryField: config.xCategoryField,
            yCategoryField: config.yCategoryField,
            valueField: config.valueField,
            dataLength: data.length
        });

        if (!config.xCategoryField || !config.yCategoryField || !config.valueField) {
            console.log('[HEATMAP] Missing required fields, returning empty');
            return [];
        }

        const xField = config.xCategoryField;
        const yField = config.yCategoryField;
        const valueField = config.valueField;
        const imageField = config.imageField;

        console.log('[HEATMAP] Sample data row:', data[0]);
        console.log('[HEATMAP] Looking for fields:', { xField, yField, valueField });

        // 1. Extract Unique Categories
        const xCategories = Array.from(new Set(data.map(d => String(d[xField])))).filter(Boolean).sort();
        const yCategories = Array.from(new Set(data.map(d => String(d[yField])))).filter(Boolean).sort();

        console.log('[HEATMAP] Categories found:', { xCount: xCategories.length, yCount: yCategories.length });

        // 2. Map existing data for quick lookup
        const dataMap = new Map<string, any>();
        data.forEach(row => {
            const key = `${row[xField]}::${row[yField]}`;
            dataMap.set(key, row);
        });

        // 3. Generate Cartesian Product
        const gridData: any[] = [];
        xCategories.forEach(xCat => {
            yCategories.forEach(yCat => {
                const key = `${xCat}::${yCat}`;
                const row = dataMap.get(key);
                const value = row ? parseFloat(row[valueField]) : 0;

                gridData.push({
                    x: xCat,
                    y: yCat,
                    value: isNaN(value) ? 0 : value,
                    image: imageField && row ? row[imageField] : undefined,
                    original: row
                });
            });
        });

        return gridData;
    }

    if (type === 'number') {
        let value: number;
        // Number chart usually has one measure or uses legacy yField/aggregation
        const activeMeasure = measures.find(m => m.visible) || (ySeries && ySeries[0]);

        if (activeMeasure) {
            const vals = data.map(row => parseFloat(row[activeMeasure.field])).filter(v => !isNaN(v));
            if (vals.length === 0) return [{ value: 0 }];
            switch (activeMeasure.aggregation) {
                case 'sum': value = vals.reduce((s, v) => s + v, 0); break;
                case 'avg': value = vals.reduce((s, v) => s + v, 0) / vals.length; break;
                case 'min': value = Math.min(...vals); break;
                case 'max': value = Math.max(...vals); break;
                case 'count': value = data.length; break;
                default: value = 0;
            }
        } else if (aggregation === 'count') {
            value = data.length;
        } else {
            // Legacy fallback
            if (!yField) return [{ value: 0 }];
            const values = data.map(row => parseFloat(row[yField])).filter(v => !isNaN(v));
            if (values.length === 0) return [{ value: 0 }];
            switch (aggregation) {
                case 'sum': value = values.reduce((s, v) => s + v, 0); break;
                case 'avg': value = values.reduce((s, v) => s + v, 0) / values.length; break;
                case 'min': value = Math.min(...values); break;
                case 'max': value = Math.max(...values); break;
                default: value = 0;
            }
        }
        return [{ value }];
    }

    if (!xField) return [];

    // Facet handling
    let facetValues: string[] = [];
    if (facets?.field) {
        const uniqueFacets = new Set(data.map(row => String(row[facets.field])));
        facetValues = Array.from(uniqueFacets).sort().slice(0, FACET_LIMIT);
    }

    // Determine active measures
    // If measures exist, use them. Else fallback to ySeries (legacy).
    let activeMeasures: any[] = measures.filter(m => m.visible);

    // Fallback to ySeries if no measures
    if (activeMeasures.length === 0 && ySeries && ySeries.length > 0) {
        activeMeasures = ySeries.filter(s => s.visible).map(s => ({
            id: s.field, // Use field as ID for legacy
            field: s.field,
            aggregation: s.aggregation,
            displayName: s.label || s.field
        }));
    }

    // Fallback to yField if nothing else
    if (activeMeasures.length === 0 && yField) {
        activeMeasures.push({
            id: 'legacy_y',
            field: yField,
            aggregation: aggregation || 'sum',
            displayName: 'Value'
        });
    }

    // Helper to calculate value
    const calculateValue = (rows: any[], measure: any) => {
        console.log(`[AGGREGATION] Measure "${measure.field}" using aggregation: ${measure.aggregation}, rows: ${rows.length}`);

        if (measure.aggregation === 'count') {
            console.log(`[AGGREGATION] Count result: ${rows.length}`);
            return rows.length;
        }

        const vals = rows.map(r => parseFloat(r[measure.field])).filter(v => !isNaN(v));
        if (vals.length === 0) {
            console.log(`[AGGREGATION] No valid numeric values found for field "${measure.field}"`);
            return 0;
        }

        const aggType = String(measure.aggregation || 'sum').toLowerCase().trim();
        let result: number;

        switch (aggType) {
            case 'sum':
                result = vals.reduce((a, b) => a + b, 0);
                break;
            case 'avg':
            case 'average':
                result = vals.reduce((a, b) => a + b, 0) / vals.length;
                break;
            case 'min':
                // spread operator ...vals can crash stack on large data, use reduce
                result = vals.reduce((min, val) => Math.min(min, val), Infinity);
                break;
            case 'max':
                // spread operator ...vals can crash stack on large data, use reduce
                result = vals.reduce((max, val) => Math.max(max, val), -Infinity);
                break;
            default:
                console.warn(`[AGGREGATION] Unknown aggregation type: "${measure.aggregation}" (normalized: "${aggType}"), defaulting to sum`);
                result = vals.reduce((a, b) => a + b, 0);
        }
        console.log(`[AGGREGATION] ${aggType} result: ${result}`);
        return result;
    };

    // Helper to aggregate a set of rows
    const aggregateRows = (rows: Record<string, any>[]) => {
        const groupedByX = rows.reduce((acc, row) => {
            const key = row[xField];
            if (key === null || key === undefined) return acc;
            if (!acc[key]) acc[key] = [];
            acc[key].push(row);
            return acc;
        }, {} as Record<string, any[]>);

        return Object.entries(groupedByX).map(([name, xRows]) => {
            const item: any = { name };

            // Handle Color Encoding
            if (encoding?.color?.field) {
                const colorField = encoding.color.field;
                console.log('[dataService] Color encoding active:', { colorField, xRowsLength: xRows.length });
                const groupedByColor = xRows.reduce((acc, row) => {
                    const colorVal = row[colorField] ?? '(No Value)';
                    if (!acc[colorVal]) acc[colorVal] = [];
                    acc[colorVal].push(row);
                    return acc;
                }, {} as Record<string, any[]>);

                Object.entries(groupedByColor).forEach(([colorVal, colorRows]) => {
                    activeMeasures.forEach(m => {
                        // Composite key: measureId::colorValue
                        // We use measure.id if available, else field (legacy)
                        const key = m.id || m.field;
                        item[`${key}::${colorVal}`] = calculateValue(colorRows as any[], m);
                    });
                });
            } else if (encoding?.detail?.field) {
                // Handle Detail Encoding (like Color By but same color)
                const detailField = encoding.detail.field;
                console.log('[dataService] Detail encoding active:', { detailField, xRowsLength: xRows.length });
                const groupedByDetail = xRows.reduce((acc, row) => {
                    const detailVal = row[detailField] ?? '(No Value)';
                    if (!acc[detailVal]) acc[detailVal] = [];
                    acc[detailVal].push(row);
                    return acc;
                }, {} as Record<string, any[]>);

                Object.entries(groupedByDetail).forEach(([detailVal, detailRows]) => {
                    activeMeasures.forEach(m => {
                        // Composite key: measureId@@detailValue (use @@ to distinguish from color ::)
                        const key = m.id || m.field;
                        item[`${key}@@${detailVal}`] = calculateValue(detailRows as any[], m);
                    });
                });
            } else {
                activeMeasures.forEach(m => {
                    const key = m.id || m.field;
                    item[key] = calculateValue(xRows, m);
                });
            }

            // Handle Label
            if (encoding?.label?.field) {
                item._label = xRows[0][encoding.label.field];
                console.log('[dataService] Label encoding:', { labelField: encoding.label.field, labelValue: item._label });
            }

            return item;
        }).sort((a, b) => {
            // Default sort by name (X-axis)
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
        });
    };

    if (facets?.field && facetValues.length > 0) {
        // Return data grouped by facet
        return facetValues.map(facetVal => {
            const facetRows = data.filter(row => String(row[facets.field]) === facetVal);
            return {
                facet: facetVal,
                data: aggregateRows(facetRows)
            };
        });
    } else {
        return aggregateRows(data);
    }
};

// Helper function to join datasets
export const joinDatasets = (
    allDatasets: Dataset[],
    allRelationships: DataRelationship[],
    targetDatasetIds: string[]
): Record<string, any>[] => {
    if (targetDatasetIds.length === 0) return [];

    const datasetsById = new Map(allDatasets.map(d => [d.id, d] as const));
    const datasetsByName = new Map(allDatasets.map(d => [d.name, d] as const));

    const targetDatasets = targetDatasetIds.map(id => datasetsById.get(id)).filter((d): d is Dataset => !!d);
    if (targetDatasets.length === 0) return [];

    // Single dataset optimization
    if (targetDatasets.length === 1) {
        const dataset = targetDatasets[0];
        // Apply Data Prep transformations and return rows
        return getTransformedData(dataset).map(row => ({ ...row }));
    }

    // Build adjacency graph
    const neighbors = new Map<string, { otherId: string, fieldFromThis: string, fieldFromOther: string }[]>();
    const addEdge = (aId: string, bId: string, aField: string, bField: string) => {
        if (!datasetsById.has(aId) || !datasetsById.has(bId)) return;
        if (!neighbors.has(aId)) neighbors.set(aId, []);
        if (!neighbors.has(bId)) neighbors.set(bId, []);
        neighbors.get(aId)!.push({ otherId: bId, fieldFromThis: aField, fieldFromOther: bField });
        neighbors.get(bId)!.push({ otherId: aId, fieldFromThis: bField, fieldFromOther: aField });
    };

    // Explicit relationships
    for (const r of allRelationships) {
        addEdge(r.datasetAId, r.datasetBId, r.fieldA, r.fieldB);
    }

    // Inferred relationships
    for (let i = 0; i < allDatasets.length; i++) {
        for (let j = i + 1; j < allDatasets.length; j++) {
            const A = allDatasets[i];
            const B = allDatasets[j];
            for (const ac of A.schema) {
                for (const bc of B.schema) {
                    const sameName = ac.name.toLowerCase() === bc.name.toLowerCase();
                    const bothNumeric = ac.type === 'number' && bc.type === 'number';
                    const bothStringLike = (ac.type !== 'number') && (bc.type !== 'number');
                    if (sameName && (bothNumeric || bothStringLike)) {
                        addEdge(A.id, B.id, ac.name, bc.name);
                    }
                }
            }
        }
    }

    // Start with the first dataset in the list as the base
    const baseDataset = targetDatasets[0];
    const baseDataTransformed = getTransformedData(baseDataset);
    let leftRows: Record<string, any>[] = baseDataTransformed.map(row => {
        const out: Record<string, any> = {};
        for (const key in row) out[`${baseDataset.name}.${key}`] = row[key];
        return out;
    });
    const included = new Set<string>([baseDataset.id]);

    const leftJoinEdge = (leftDatasetId: string, rightDatasetId: string, leftField: string, rightField: string) => {
        const leftDataset = datasetsById.get(leftDatasetId)!;
        const rightDataset = datasetsById.get(rightDatasetId)!;
        const rightDataTransformed = getTransformedData(rightDataset);

        const rightIndex = new Map<any, Record<string, any>>();
        for (const r of rightDataTransformed) rightIndex.set(r[rightField], r);

        const newLeftRows: Record<string, any>[] = leftRows.map(lr => {
            const joinVal = lr[`${leftDataset.name}.${leftField}`];
            const rr = rightIndex.get(joinVal);
            const merged: Record<string, any> = { ...lr };
            if (rr) {
                for (const key in rr) merged[`${rightDataset.name}.${key}`] = rr[key];
            } else {
                rightDataset.schema.forEach(col => {
                    merged[`${rightDataset.name}.${col.name}`] = null;
                });
            }
            return merged;
        });
        leftRows = newLeftRows;
        included.add(rightDatasetId);
    };

    const findPath = (startIds: Set<string>, targetId: string): { from: string, to: string, leftField: string, rightField: string }[] | null => {
        const queue: string[] = Array.from(startIds);
        const prev = new Map<string, { parent: string, leftField: string, rightField: string }>();
        const visited = new Set(queue);
        let found: string | null = null;
        while (queue.length) {
            const cur = queue.shift()!;
            if (!neighbors.has(cur)) continue;
            for (const e of neighbors.get(cur)!) {
                if (visited.has(e.otherId)) continue;
                visited.add(e.otherId);
                prev.set(e.otherId, { parent: cur, leftField: e.fieldFromThis, rightField: e.fieldFromOther });
                if (e.otherId === targetId) { found = e.otherId; queue.length = 0; break; }
                queue.push(e.otherId);
            }
        }
        if (!found) return null;
        const edges: { from: string, to: string, leftField: string, rightField: string }[] = [];
        let cur = found;
        while (!startIds.has(cur)) {
            const info = prev.get(cur)!;
            edges.push({ from: info.parent, to: cur, leftField: info.leftField, rightField: info.rightField });
            cur = info.parent;
        }
        edges.reverse();
        return edges;
    };

    for (const ds of targetDatasets) {
        if (included.has(ds.id)) continue;
        const path = findPath(included, ds.id);
        if (!path) {
            const names = targetDatasets.map(d => `"${d.name}"`).join(', ');
            throw new Error(`No relationship path connects all selected datasets (${names}). Please add relationships to link them.`);
        }
        for (const edge of path) {
            if (included.has(edge.to)) continue;
            leftJoinEdge(edge.from, edge.to, edge.leftField, edge.rightField);
        }
    }

    return leftRows;
};

export const processDataForChart = (
    allDatasets: Dataset[],
    allRelationships: DataRelationship[],
    config: ChartConfig
): any[] => {
    console.log('processDataForChart called with config:', config);

    // Determine which datasets are actually referenced by fields
    const usedFields = [
        config.xField,
        config.yField,
        ...(config.measures?.map(m => m.field) || []),
        ...(config.ySeries?.map(s => s.field) || []),
        config.facets?.field,
        config.encoding?.color?.field,
        config.encoding?.label?.field,
        config.encoding?.detail?.field,
        config.imageField,
        // Heatmap-specific fields
        config.xCategoryField,
        config.yCategoryField,
        config.valueField,
        // Advanced fields
        config.advanced?.vector?.uField,
        config.advanced?.vector?.vField,
        config.advanced?.threeD?.xField,
        config.advanced?.threeD?.yField,
        config.advanced?.threeD?.zField,
        // Globe fields
        config.advanced?.globe?.latField,
        config.advanced?.globe?.lonField,
        config.advanced?.globe?.valueField,
        config.advanced?.globe?.categoryField,
        // Map fields
        config.advanced?.map?.latField,
        config.advanced?.map?.lonField,
        config.latField, // Legacy
        config.lonField, // Legacy
        config.iso3Field,
        config.countryField
    ].filter(Boolean) as string[];
    console.log('Used fields:', usedFields);

    // If no fields are selected yet, but datasets are, return empty to await field selection.
    if (usedFields.length === 0 && config.datasetIds.length > 0) {
        // For number chart with count, we might not need fields, but we need datasets
        if (config.type === 'number' && (config.aggregation === 'count' || config.measures?.some(m => m.aggregation === 'count'))) {
            // pass through
        } else {
            console.log('No fields selected, returning empty');
            return [];
        }
    }

    const fieldInfo = usedFields.map(f => {
        const parts = f.split('.');
        if (parts.length < 2) return { datasetName: '', fieldName: f, original: f };
        const datasetName = parts[0];
        const fieldName = parts.slice(1).join('.');
        return { datasetName, fieldName, original: f };
    });
    console.log('Field info:', fieldInfo);

    const usedDatasetNames = [...new Set(fieldInfo.map(f => f.datasetName).filter(n => n))];
    const datasetsByName = new Map(allDatasets.map(d => [d.name, d] as const));

    // If we have specific fields used, restrict to those datasets. 
    // Otherwise (e.g. count of rows), use all selected datasets from config.
    let targetDatasetIds: string[] = [];
    if (usedDatasetNames.length > 0) {
        targetDatasetIds = usedDatasetNames.map(name => datasetsByName.get(name)?.id).filter((id): id is string => !!id);
    } else {
        targetDatasetIds = config.datasetIds;
    }
    console.log('Target dataset IDs:', targetDatasetIds);

    if (targetDatasetIds.length === 0) {
        console.log('No target datasets, returning empty');
        return [];
    }

    try {
        const joinedData = joinDatasets(allDatasets, allRelationships, targetDatasetIds);
        // console.log('Joined data:', joinedData.slice(0, 3)); // Show first 3 rows
        const result = runAggregation(joinedData, config);
        // console.log('Aggregation result:', result);
        return result;
    } catch (e) {
        console.error("Data processing error:", e);
        return [];
    }
};


export const processDataForAdvancedChart = (
    datasets: Dataset[],
    relationships: DataRelationship[],
    config: Omit<ChartConfig, 'id' | 'createdAt'>
): any => {
    // Use joinDatasets to get combined data if multiple datasets are selected
    let data: Record<string, any>[] = [];

    if (config.datasetIds.length > 0) {
        try {
            data = joinDatasets(datasets, relationships, config.datasetIds);
        } catch (e) {
            console.error("Join failed for advanced chart:", e);
            return [];
        }
    } else {
        return [];
    }

    switch (config.type) {
        case 'globe':
            if (!config.latField || !config.lonField || !config.valueField) return [];
            return data
                .map(row => ({
                    lat: parseFloat(row[config.latField!]),
                    lng: parseFloat(row[config.lonField!]),
                    value: parseFloat(row[config.valueField!]),
                    name: `Lat: ${parseFloat(row[config.latField!]).toFixed(2)}, Lng: ${parseFloat(row[config.lonField!]).toFixed(2)}`
                }))
                .filter(row => !isNaN(row.lat) && !isNaN(row.lng) && !isNaN(row.value));
        case 'map':
            if ((!config.iso3Field && !config.countryField) || !config.valueField) return [];
            return data.map(row => ({
                iso3: config.iso3Field ? row[config.iso3Field] : undefined,
                country: config.countryField ? row[config.countryField] : undefined,
                value: parseFloat(row[config.valueField!]),
                name: config.iso3Field ? row[config.iso3Field] : row[config.countryField!],
                lat: row['lat'] ? parseFloat(row['lat']) : undefined,
                lng: row['lng'] ? parseFloat(row['lng']) : undefined
            })).filter(row => !isNaN(row.value));
        case 'force-directed':
            if (!config.sourceField || !config.targetField) return { nodes: [], links: [] };

            const nodesMap = new Map<string, any>();
            const links: any[] = [];

            data.forEach(row => {
                const source = String(row[config.sourceField!]);
                const target = String(row[config.targetField!]);

                if (!source || !target || source === 'undefined' || target === 'undefined') return;

                if (!nodesMap.has(source)) {
                    nodesMap.set(source, { id: source, label: source });
                }
                if (!nodesMap.has(target)) {
                    nodesMap.set(target, { id: target, label: target });
                }

                links.push({
                    source,
                    target,
                    value: 1 // Default weight
                });
            });

            return {
                nodes: Array.from(nodesMap.values()),
                links
            };

        case '3d-bar':
            if (!config.xField || !config.yField) return [];

            // Perform aggregation: Sum yField by xField (and zField if present)
            const grouped = new Map<string, any>();
            const xField = config.xField;
            const yField = config.yField;
            const zField = config.advanced?.threeD?.zField || (config as any).zField;

            data.forEach(row => {
                const xVal = String(row[xField]);
                const zVal = zField ? String(row[zField]) : '';
                const key = `${xVal}::${zVal}`;

                if (!grouped.has(key)) {
                    grouped.set(key, {
                        [xField]: xVal,
                        [yField]: 0,
                        ...(zField ? { [zField]: zVal } : {}),
                        _count: 0
                    });
                }

                const item = grouped.get(key);
                const val = parseFloat(row[yField]);
                if (!isNaN(val)) {
                    item[yField] += val;
                    item._count++;
                }
            });

            return Array.from(grouped.values());

        case 'scientific':
        case 'vector': {
            // For scientific charts, read from fieldMappings in advanced.scientific config
            const scientificConfig = config.advanced?.scientific;
            const fieldMappings = scientificConfig?.fieldMappings || {};

            // Use fieldMappings if available, fall back to top-level config for compatibility
            const xField = fieldMappings.xField || config.xField;
            const yField = fieldMappings.yField || config.yField;

            if (!xField || !yField) return [];

            return data.map(row => {
                const xVal = row[xField];
                const yVal = parseFloat(row[yField]);

                // Parsing X: Try to parse as float, but keep original if it fails (likely categorical)
                // Note: parseFloat('123') = 123, parseFloat('abc') = NaN
                const parsedX = parseFloat(xVal);
                const x = (!isNaN(parsedX) && isFinite(parsedX)) ? parsedX : xVal;

                const item: any = {
                    // Include ALL original row data so fields are accessible by their names
                    ...row,
                    x,
                    y: isNaN(yVal) ? 0 : yVal,
                    original: row
                };

                // Handle vector fields if present
                if (config.type === 'vector' && config.advanced?.vector) {
                    const { uField, vField } = config.advanced.vector;
                    if (uField) item.u = parseFloat(row[uField]);
                    if (vField) item.v = parseFloat(row[vField]);
                }

                // Handle scientific additional fields
                if (config.type === 'scientific' && fieldMappings) {
                    // Color/Size fields
                    if (fieldMappings.colorField) item.colorValue = row[fieldMappings.colorField];
                    if (fieldMappings.sizeField) item.sizeValue = parseFloat(row[fieldMappings.sizeField]);
                    if (fieldMappings.seriesField) item.series = row[fieldMappings.seriesField];

                    // Vector fields (U/V components for arrow plots)
                    if (fieldMappings.uField) item.u = parseFloat(row[fieldMappings.uField]);
                    if (fieldMappings.vField) item.v = parseFloat(row[fieldMappings.vField]);

                    // Error bar fields
                    if (fieldMappings.yErrorField) item.yError = parseFloat(row[fieldMappings.yErrorField]);
                    if (fieldMappings.xErrorField) item.xError = parseFloat(row[fieldMappings.xErrorField]);
                    if (fieldMappings.yErrorLowerField) item.yErrorLower = parseFloat(row[fieldMappings.yErrorLowerField]);
                    if (fieldMappings.yErrorUpperField) item.yErrorUpper = parseFloat(row[fieldMappings.yErrorUpperField]);

                    // Confidence interval bounds
                    if (fieldMappings.yLowerBoundField) item.yLowerBound = parseFloat(row[fieldMappings.yLowerBoundField]);
                    if (fieldMappings.yUpperBoundField) item.yUpperBound = parseFloat(row[fieldMappings.yUpperBoundField]);

                    // 3D fields
                    if (fieldMappings.zField) item.z = parseFloat(row[fieldMappings.zField]);
                }

                return item;
            }).filter(d => d.x !== undefined && d.x !== null && !isNaN(d.y));
        }

        case 'kpi-card':
            // KPI Card just needs raw data - it does its own aggregation
            return data;

        default:
            return [];
    }
};



